<template>
  <div>
    <div class="t-container ">
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="filteredTransactions"
        item-key="transactionId"
        :item-class="rowClass"
        show-select
        single-select
        :search="search"
        :loading="loading"
        :custom-sort="customSort"
        :custom-filter="customFilter"
        :options.sync="tableOptions"
        @update:page="tableProperties.page = $event"
        @update:items-per-page="tableProperties.itemsPerPage = $event"
      >
        <template v-slot:[`item.orderNumber`]="{ item }">
          <b-link @click="viewTransactionHistory(item)">
            {{ item.orderNumber }}</b-link
          >
        </template>

        <template v-slot:[`item.email`]="{ item }">
          <b-link
            v-if="item.customerId"
            @click="loadCustomerTransactions(item.customerId)"
          >
            {{ item.email }}</b-link
          >
          <span v-else>{{ item.email }}</span>
        </template>

        <template v-slot:[`item.amount`]="{ item }">
          ${{ item.amount }} {{ item.currencyText }}</template
        >

        <template v-slot:[`item.transactionStatus`]="{ item }">
          <v-chip
            v-if="item.transactionStatus"
            :color="statusColour(item.transactionStatus)"
            dark
            v-bind="statusProps(item.transactionStatus)"
            @click="showTransactionResponse(item)"
          >
            {{ statusText(item.transactionStatus) }}
          </v-chip>
        </template>

        <template v-slot:loading>
          <b-spinner variant="primary" label="Spinning"></b-spinner>
        </template>
      </v-data-table>
    </div>
    <ModifyTransaction
      :dialog="dialog"
      v-model="editedItem"
      @save="save"
      @close="close"
    />
    <ViewFACResponseModal
      :dialog="showResponse"
      :transactionId="transactionId"
      @closeFACResponseModal="showResponse = false"
    />
  </div>
</template>
<style></style>
<script>
import ModifyTransaction from "@/components/widgets/ModifyTransaction.vue";
import TransactionFilterMixin from "@/components/transaction/TransactionFilterMixin.js";
import ViewFACResponseModal from "@/components/widgets/ViewFACResponseModal.vue";
import Format from "@/services/Formatting.js";

export default {
  mixins: [TransactionFilterMixin],
  components: {
    ModifyTransaction,
    ViewFACResponseModal
  },
  props: {
    value: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    filteredStatuses: { type: Array },
    search: {
      type: String,
      required: false
    },
    transactions: {
      type: Array,
      required: true,
      default() {
        return [];
      }
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      nonHeaders: [
        "isRecurring",
        "intentId",
        "hasPromotionalCode",
        "promotionalCodeId",
        "customerId"
      ],
      standardFieldHeaders: [
        {
          text: "Order #",
          value: "orderNumber",

          align: "left",
          sortable: true,
          filter: this.textFilter("orderNumber")
        },
        {
          text: "Date",
          value: "date",
          sortable: true
        },
        {
          text: "Card",
          value: "cardType",
          align: "left",
          sortable: true,

          filter: this.textFilter("cardType")
        },
        {
          text: "Amount",
          value: "amount",

          align: "left",
          sortable: true,
          filter: this.textFilter("amount")
        },
        {
          text: "First Name",
          value: "firstName",
          align: "left",
          sortable: true,
          filter: this.textFilter("firstName")
        },
        {
          text: "Last Name",
          value: "lastName",
          align: "left",
          sortable: true,
          filter: this.textFilter("lastName")
        },
        {
          text: "Email",
          value: "email",
          align: "left",
          sortable: true,
          filter: this.textFilter("email")
        },
        {
          text: "Status",
          value: "transactionStatus",
          filter: this.filterByStatus
        }
      ],
      dialog: false,
      editedItem: {
        amount: 0.0
      },
      defaultItem: {
        amount: 0.0
      },
      editedIndex: -1,
      tableProperties: {
        page: 1,
        itemsPerPage: 10
      },
      showResponse: false,
      transactionId: ""
    };
  },
  watch: {
    statuses(value) {
      this.$emit("updateTableStatuses", value);
    },
    filteredStatuses(value) {
      this.filterModel.transactionStatus = value;
    },
    filteredTransactions(newValue) {
      this.loadMoreTransactionsCheck();
    },
    shouldLoadMoreTransactions(newValue) {
      if (newValue >= 0) this.loadMoreTransactions();
    }
  },
  computed: {
    selected: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    merchantId() {
      return this.$store.getters.merchantId;
    },
    filteredTransactions() {
      const seen = new Set();
      return this.transactions.reduce((filteredTransactions, transaction) => {
        if (
          this.filterDeclinedTransactions(transaction.transactionStatus) &&
          !seen.has(transaction.transactionId)
        ) {
          seen.add(transaction.transactionId);
          filteredTransactions.push({ ...transaction });
        }
        return filteredTransactions;
      }, []);
    },
    statuses() {
      return this.filteredTransactions.map(transaction => {
        return {
          value: transaction.transactionStatus,
          text: this.statusText(transaction.transactionStatus)
        };
      });
    },
    standardFields() {
      return [
        "transactionId",
        "transactionDate",
        "orderNumber",
        "date",
        "amount",
        "currencyText",
        "firstName",
        "lastName",
        "email",
        "customFields",
        "transactionStatus",
        "cardType",
        "data-table-select"
      ];
    },
    totalTransactions() {
      return this.filteredTransactions.length;
    },
    totalPages() {
      if (this.tableProperties.itemsPerPage === 0) return 0;
      return this.totalTransactions / this.tableProperties.itemsPerPage;
    },
    shouldLoadMoreTransactions() {
      if (this.totalPages == 0) return false;

      return this.tableProperties.page - this.totalPages;
    },
    tenantAlias() {
      return this.$store.getters.merchantId;
    }
  },
  methods: {
    loadCustomerTransactions(cid) {
      this.$emit("customerIdSearch", cid);
    },
    rowClass() {
      const rowClass = "table-rows";
      return rowClass;
    },
    loadMoreTransactionsCheck() {
      if (this.shouldLoadMoreTransactions >= 0) this.loadMoreTransactions();
    },
    loadMoreTransactions() {
      this.$emit("loadMoreTransaction");
    },
    viewTransactionHistory(item) {
      this.$emit("viewTransactionHistory", item);
    },
    editItem(item) {
      this.editedIndex = this.transactions.findIndex(
        t => t.transactionId == item.transactionId
      );
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    save() {
      if (this.editedIndex > -1) {
        this.$emit("saveModification", this.editedIndex, this.editedItem);
      }
      this.close();
    },
    updateValue(val) {
      this.$emit("input", val);
    },
    statusColour(status) {
      return !!this.transactionStatuses[status]
        ? this.transactionStatuses[status].colour
        : "black";
    },
    statusText(status) {
      if (!status) return "";
      return !!this.transactionStatuses[status]
        ? this.transactionStatuses[status].name
        : status;
    },
    statusProps(status) {
      if (
        !this.transactionStatuses[status] ||
        !this.transactionStatuses[status].props
      )
        return {};

      return this.transactionStatuses[status].props;
    },
    transformHeader(fieldName) {
      return Format.transformValueToLabel(fieldName);
    },
    refreshHeaders(object) {
      let headers = [...this.standardFieldHeaders];
      const transactionStatusHeader = headers.pop();
      for (let key in object) {
        if (
          object.hasOwnProperty(key) &&
          !this.standardFields.includes(key) &&
          !this.nonHeaders.includes(key)
        ) {
          let header = { text: this.transformHeader(key), value: key };
          header = this.setupFilters(header);
          headers.push(header);
        }
      }
      headers.push(transactionStatusHeader);
      this.headers = headers;
    },
    setupTransactionsAndHeaders(transactions) {
      this.headers = this.extractHearders(transactions[0]);
      return transactions;
    },
    showTransactionResponse(transaction) {
      this.showResponse = true;
      this.transactionId = transaction.transactionId;
    }
  },
  mounted() {
    if (!this.headers.length && !!this.transactions.length)
      this.refreshHeaders(this.transactions[0]);
  }
};
</script>

<style lang="scss" scoped></style>
